import { useMemo, useState } from "react";

import SEO from "../components/SEO";
import PageHero from "../components/shared/PageHero";
import Reveal from "../components/shared/Reveal";
import useI18n from "../i18n/useI18n";
import { trackEvent } from "../utils/analytics";

const BlogPage = () => {
  const { t } = useI18n();
  const posts = t("blog.posts");
  const allLabel = t("blog.allLabel");

  const [activeCat, setActiveCat] = useState(allLabel);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  // Unique categories in post order, prefixed with "All".
  const categories = useMemo(() => {
    const seen = [];
    (Array.isArray(posts) ? posts : []).forEach((p) => {
      if (!seen.includes(p.cat)) seen.push(p.cat);
    });
    return [allLabel, ...seen];
  }, [posts, allLabel]);

  const countOf = (cat) =>
    cat === allLabel ? posts.length : posts.filter((p) => p.cat === cat).length;

  const visible = activeCat === allLabel ? posts : posts.filter((p) => p.cat === activeCat);

  const onSubscribe = (event) => {
    event.preventDefault();
    if (!email.trim()) return;
    setSubscribed(true);
    trackEvent("newsletter_subscribe", { source: "blog" });
  };

  return (
    <div className="blog-page">
      <SEO title={`CodeIL · ${t("nav.blog")}`} description={t("blog.lede")} path="/blog" />

      <PageHero eyebrow={t("blog.eyebrow")} titleWords={t("blog.title")} lede={t("blog.lede")}>
        <Reveal as="form" className="newsletter" onSubmit={onSubscribe}>
          <div className="newsletter__label">{t("blog.newsletterLabel")}</div>
          <p className="newsletter__text">{t("blog.newsletterText")}</p>
          <div className="newsletter__row">
            <input
              type="email"
              required
              className="newsletter__input"
              placeholder={t("blog.newsletterPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={subscribed}
            />
            <button type="submit" className="newsletter__btn" disabled={subscribed}>
              {subscribed ? t("blog.newsletterDone") : t("blog.newsletterSubmit")}
            </button>
          </div>
        </Reveal>
      </PageHero>

      <section className="section blog-list">
        <div className="blog-list__inner">
          <div className="blog-filters">
            <div className="blog-filters__chips">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`blog-chip${activeCat === cat ? " is-active" : ""}`}
                  onClick={() => setActiveCat(cat)}
                >
                  {cat === allLabel ? allLabel : cat}
                  <span className="blog-chip__n">{countOf(cat)}</span>
                </button>
              ))}
            </div>
            <span className="blog-filters__count">
              {visible.length} {visible.length === 1 ? t("blog.countOne") : t("blog.countMany")}
            </span>
          </div>

          {/* Featured */}
          <Reveal as="a" href="#" className="featured-post" onClick={(e) => e.preventDefault()}>
            <div className="featured-post__body">
              <div className="featured-post__meta">
                <span className="featured-post__badge">{t("blog.featuredBadge")}</span>
                <span className="featured-post__cat">{t("blog.featuredMeta")}</span>
              </div>
              <h2 className="featured-post__title">{t("blog.featuredTitle")}</h2>
              <p className="featured-post__excerpt">{t("blog.featuredExcerpt")}</p>
              <span className="featured-post__cta">
                {t("blog.featuredCta")} <span className="featured-post__arrow">←</span>
              </span>
            </div>
            <div className="media-frame media-frame--feature" aria-hidden="true">
              <span className="media-frame__tag">FEATURED</span>
            </div>
          </Reveal>

          {/* Grid */}
          {visible.length > 0 ? (
            <div className="post-grid">
              {visible.map((post, i) => (
                <Reveal as="a" href="#" className="post-card" key={i} onClick={(e) => e.preventDefault()}>
                  <div className="media-frame media-frame--169" aria-hidden="true">
                    <span className="post-card__cat">{post.cat}</span>
                  </div>
                  <div className="post-card__body">
                    <h3 className="post-card__title">{post.title}</h3>
                    <p className="post-card__excerpt">{post.excerpt}</p>
                    <div className="post-card__meta">
                      <span>{post.date}</span>
                      <span className="post-card__dot">·</span>
                      <span>{post.read} {t("blog.readSuffix")}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="post-empty">{t("blog.emptyText")}</div>
          )}
        </div>
      </section>

      <section className="cta-banner">
        <div className="cta-banner__wash" aria-hidden="true" />
        <div className="cta-banner__inner">
          <h2 className="cta-banner__title">{t("blog.ctaTitle")}</h2>
          <p className="cta-banner__lede">{t("blog.ctaLede")}</p>
          <div className="cta-banner__actions">
            <a href="mailto:info@codeil.org" className="cta-banner__btn cta-banner__btn--primary">
              {t("blog.ctaButton")}
              <span className="cta-banner__arrow">←</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
